// Calendar rules shared by the editor preview and the API availability reader.
export type OnCallRotationRule = {
  name: string;
  startDate: string;
  endDate: string | null;
  technicianId: string;
  coverageEndDate: string;
  repeats: "never" | "daily" | "weekly";
  repeatEvery: number;
  hours: Array<{ day: number; start: string; end: string }>;
};
export type OnCallRotation = OnCallRotationRule & { id: string; timezone: string; revision: number; stoppedAt: string | null };
const DAY = 86_400_000;
// API reads stay small, but the editor also uses this helper to preview a
// complete valid one-time rotation (which may span up to 93 days).
export const MAX_SCHEDULE_READ_RANGE_DAYS = 31;
const MAX_ROTATION_EXPANSION_RANGE_DAYS = 93;
export function rotationDate(date: string, days: number) {
  return new Date(Date.parse(`${date}T12:00:00Z`) + days * DAY).toISOString().slice(0, 10);
}
function localParts(instant: string, timezone: string) {
  return Object.fromEntries(new Intl.DateTimeFormat("en-CA", { timeZone: timezone, year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).formatToParts(new Date(instant)).map((part) => [part.type, part.value]));
}
export function rotationLocalDate(instant: string, timezone: string) {
  const p = localParts(instant, timezone);
  return `${p.year}-${p.month}-${p.day}`;
}
export function rotationInstant(date: string, time: string, timezone: string) {
  const desired = Date.parse(`${date}T${time}:00Z`);
  let guess = desired;
  for (let i = 0; i < 4; i++) {
    const p = localParts(new Date(guess).toISOString(), timezone);
    const actual = Date.parse(`${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}:00Z`);
    const offset = desired - actual;
    if (!offset) break;
    guess += offset;
  }
  // A nonexistent spring-forward time advances to the first corresponding real
  // instant. Rules still advance in local calendar days, never fixed 24h periods.
  return new Date(guess).toISOString();
}
export function rotationSpanDays(rule: OnCallRotationRule) {
  return Math.round((Date.parse(`${rule.coverageEndDate}T12:00:00Z`) - Date.parse(`${rule.startDate}T12:00:00Z`)) / DAY) + 1;
}
export function rotationPeriodDays(rule: OnCallRotationRule) {
  return rule.repeats === "never" ? rotationSpanDays(rule) : rule.repeatEvery * (rule.repeats === "weekly" ? 7 : 1);
}
export type RotationWindow = { id: string; rotationId: string; technicianId: string; startsAt: string; endsAt: string; kind: "on_call"; title: string; allDay: boolean; paid: boolean; status: string; note: string | null };
export function expandRotation(rotation: OnCallRotation, startsAt: string, endsAt: string): RotationWindow[] {
  if (endsAt <= startsAt || Date.parse(endsAt) - Date.parse(startsAt) > MAX_ROTATION_EXPANSION_RANGE_DAYS * DAY) throw new Error(`Expand on-call rotations in ranges of at most ${MAX_ROTATION_EXPANSION_RANGE_DAYS} days.`);
  const from = rotationDate(rotationLocalDate(startsAt, rotation.timezone), -1);
  const until = rotationDate(rotationLocalDate(endsAt, rotation.timezone), 1);
  const result: RotationWindow[] = [];
  for (let date = from > rotation.startDate ? from : rotation.startDate; date <= until; date = rotationDate(date, 1)) {
    if (rotation.endDate && date > rotation.endDate) break;
    const elapsedDays = Math.round((Date.parse(`${date}T12:00:00Z`) - Date.parse(`${rotation.startDate}T12:00:00Z`)) / DAY);
    const coverageDays = rotationSpanDays(rotation);
    const periodDays = rotationPeriodDays(rotation);
    if (rotation.repeats === "never" ? elapsedDays >= coverageDays : elapsedDays % periodDays >= coverageDays) continue;
    const day = new Date(`${date}T12:00:00Z`).getUTCDay();
    const hours = rotation.hours.find(hours => hours.day === day);
    if (!hours) continue;
    const start = rotationInstant(date, hours.start, rotation.timezone);
    let end = rotationInstant(rotationDate(date, hours.end <= hours.start ? 1 : 0), hours.end, rotation.timezone);
    if (rotation.stoppedAt && end > rotation.stoppedAt) end = rotation.stoppedAt;
    if (start >= end || start >= endsAt || end <= startsAt) continue;
    result.push({ id: `rotation:${rotation.id}:${date}`, rotationId: rotation.id, technicianId: rotation.technicianId, startsAt: start, endsAt: end, kind: "on_call", title: rotation.name, allDay: false, paid: false, status: "active", note: "Recurring on-call availability" });
  }
  return result;
}

export function upcomingRotationCoverage(rotation: OnCallRotation, fromDate: string, count = 10) {
  const periodDays = rotationPeriodDays(rotation);
  const elapsedDays = Math.max(0, Math.round((Date.parse(`${fromDate}T12:00:00Z`) - Date.parse(`${rotation.startDate}T12:00:00Z`)) / DAY));
  const cycleStart = rotationDate(rotation.startDate, Math.floor(elapsedDays / periodDays) * periodDays);
  const earliest = rotationInstant(fromDate, "00:00", rotation.timezone);
  const result: RotationWindow[] = [];
  // Jump directly between coverage ranges, including infrequent recurrences.
  for (let index = 0; index <= count && result.length < count; index++) {
    if (rotation.repeats === "never" && index > 0) break;
    const date = rotation.repeats === "never" ? rotation.startDate : rotationDate(cycleStart, index * periodDays);
    if (rotation.endDate && date > rotation.endDate) break;
    const start = rotationInstant(date, "00:00", rotation.timezone);
    if (rotation.stoppedAt && start >= rotation.stoppedAt) break;
    result.push(...expandRotation(rotation, start, rotationInstant(rotationDate(date, Math.min(rotationSpanDays(rotation), periodDays)), "00:00", rotation.timezone)).filter(window => window.startsAt >= earliest && window.startsAt >= start));
  }
  return result.slice(0, count);
}
